import type {
  QueryQuestionEntriesResponse,
  QuestionEntry,
} from '@wix/ambassador-faq-question-entry-v2-question-entry/types';

// Review: make the category filter match the values accepted by its call sites.
export type CategoryId = string | string[] | undefined;

export type GetListQuestions = (params: {
  categoryId: CategoryId;
  cursor?: string;
  prev?: QuestionEntry[];
  suppressError?: boolean;
}) => Promise<QueryQuestionEntriesResponse>;

// Review: bound deep-link prefetching so initialization cannot fetch indefinitely.
const MAX_PREFETCH_PAGES = 20;

export const loadPaginatedQuestionsUntilTarget = async ({
  categoryId,
  targetQuestionId,
  getListQuestions,
}: {
  categoryId: CategoryId;
  targetQuestionId?: string | null;
  getListQuestions: GetListQuestions;
}): Promise<QuestionEntry[]> => {
  let result = await getListQuestions({ categoryId });
  let questionEntries = result.questionEntries || [];
  let prefetchedPageCount = 0;
  const seenCursors = new Set<string>();

  while (
    targetQuestionId &&
    !questionEntries.some(({ id }) => id === targetQuestionId) &&
    result.pagingMetadata?.hasNext &&
    prefetchedPageCount < MAX_PREFETCH_PAGES
  ) {
    const nextCursor = result.pagingMetadata.cursors?.next;
    if (!nextCursor || seenCursors.has(nextCursor)) {
      break;
    }

    seenCursors.add(nextCursor);
    prefetchedPageCount += 1;

    try {
      result = await getListQuestions({
        categoryId,
        cursor: nextCursor,
        prev: questionEntries,
        suppressError: true,
      });
      questionEntries = [...questionEntries, ...(result.questionEntries || [])];
    } catch {
      return questionEntries;
    }
  }

  return questionEntries;
};
